import React, { CSSProperties } from "react";
import {
  GenericControllerProvider,
  GenericControllerProviderProps,
} from "../../forms/common/GenericControllerProvider";
import {
  GenericVirtualizedEntitySelectFormField,
  GenericVirtualizedEntitySelectFormFieldProps,
} from "./GenericVirtualizedEntitySelectFormField";
import {
  IEntityMinimalModel,
  IGenericRequestParameters,
  IOwnedEntityParameters,
  ISoftDeletable,
  IdTypes,
  InferSortByOptions,
  StringIndexedDict,
  TypedEntitySuggestionsModel,
} from "../../../api/GenericTypes";
import { FormFieldError, FormFieldLayout } from "../FormFieldLayouts";
import { toUppercase } from "../../helperfunctions/stringFunctions";
import styles from "../formfields.module.css";
import { FieldErrors, GlobalError, Path, PathValue } from "react-hook-form";
import { FormGroup } from "react-bootstrap";

// Stripped down entity interface

export interface GenericEntityVirtualizedEntitySelectFormFieldProps<
  Entity extends StringIndexedDict,
  SuggestionsEntity extends (Omit<IEntityMinimalModel<IdTypes>, "version"> | TypedEntitySuggestionsModel<IdTypes>) &
    Partial<ISoftDeletable>,
  SuggestionsEntityFilters extends StringIndexedDict &
    IOwnedEntityParameters &
    IGenericRequestParameters<SuggestionsEntity, InferSortByOptions<SuggestionsEntityFilters>>,
> extends Omit<
  GenericVirtualizedEntitySelectFormFieldControllerWrapperProps<Entity, SuggestionsEntity, SuggestionsEntityFilters>,
  "entityConstants" | "orderByOptions"
> {}

// Pure interface
/**
 * Props for the GenericVirtualizedEntitySelectFormFieldControllerWrapper component.
 * @author @CorradoSurmanowicz
 * @template Entity - The type of the entity.
 * @template SuggestionsEntity - The type of the suggestions entity.
 * @template SuggestionsEntityFilters - The type of the suggestions entity filters.
 *
 * @property {boolean} [required] - Indicates if the field is required.
 * @property {boolean} [horizontal] - Indicates if the field should be displayed horizontally.
 * @property {boolean} [hasLabel] - Indicates if the field has a label.
 * @property {string} [label] - The label for the field.
 * @property {boolean} [uncontained] - Indicates if the field should be uncontained.
 * @property {PathValue<Entity, Path<Entity>>} [defaultValue] - The default value for the field.
 * @property {CSSProperties} [formStyle] - The style for the form.
 * @property {CSSProperties} [style] - The style for the field.
 * @property {((props: { value: PathValue<Entity, Path<Entity>>; onChange: (...event: any[]) => void; }) => React.ReactNode) | React.ReactNode} [children] - The children to render within the field.
 * @property {(value: any, onChange: (...event: any[]) => void) => void} [onChangeOverride] - A function to override the default onChange behavior.
 * @property {((props: { value: PathValue<Entity, Path<Entity>>; onChange: (...event: any[]) => void; }) => React.ReactNode) | React.ReactNode} [prependChildren] - Indicates if the children should be prepended.
 */
export interface GenericVirtualizedEntitySelectFormFieldControllerWrapperProps<
  Entity extends StringIndexedDict,
  SuggestionsEntity extends (Omit<IEntityMinimalModel<IdTypes>, "version"> | TypedEntitySuggestionsModel<IdTypes>) &
    Partial<ISoftDeletable>,
  SuggestionsEntityFilters extends StringIndexedDict &
    IOwnedEntityParameters &
    IGenericRequestParameters<SuggestionsEntity, InferSortByOptions<SuggestionsEntityFilters>>,
>
  extends
    Omit<GenericControllerProviderProps<Entity>, "children">,
    Omit<
      GenericVirtualizedEntitySelectFormFieldProps<Entity, SuggestionsEntity, SuggestionsEntityFilters>,
      "onChange" | "value" | "error"
    > {
  required?: boolean;
  horizontal?: boolean;
  hasLabel?: boolean;
  label?: string;
  uncontained?: boolean;
  defaultValue?: PathValue<Entity, Path<Entity>>;
  formStyle?: CSSProperties;
  style?: CSSProperties;
  children?:
    | (({
        value,
        onChange,
      }: {
        value: PathValue<Entity, Path<Entity>>;
        onChange: (...event: any[]) => void;
      }) => React.ReactNode)
    | React.ReactNode;
  onChangeOverride?: (value: any, onChange: (...event: any[]) => void) => void;
  prependChildren?:
    | (({
        value,
        onChange,
      }: {
        value: PathValue<Entity, Path<Entity>>;
        onChange: (...event: any[]) => void;
      }) => React.ReactNode)
    | React.ReactNode;
}

export const getError = <Entity extends StringIndexedDict>(id: Path<Entity>, errors: FieldErrors<Entity>) => {
  // Support dot and bracket notation (e.g. 'sections[1].name' or 'sections.1.name')
  const fieldPath = id.replace(/\[(\d+)\]/g, ".$1").split(".");
  let error: any = errors;
  for (const path of fieldPath) {
    if (error == null) return undefined;
    error = error[path];
  }
  return (error as GlobalError) ?? undefined;
};

/**
 * Wrapper component for the GenericVirtualizedEntitySelectFormFieldController.
 * @author @CorradoSurmanowicz
 * @template Entity - The type of the entity.
 * @template SuggestionsEntity - The type of the suggestions entity.
 * @template SuggestionsEntityFilters - The type of the suggestions entity filters.
 *
 * @param props - The props for the GenericVirtualizedEntitySelectFormFieldControllerWrapper.
 *
 * @returns The rendered GenericVirtualizedEntitySelectFormFieldControllerWrapper component.
 */
export const GenericVirtualizedEntitySelectFormFieldControllerWrapper = <
  Entity extends StringIndexedDict,
  SuggestionsEntity extends IEntityMinimalModel<IdTypes> | TypedEntitySuggestionsModel<IdTypes>,
  SuggestionsEntityFilters extends StringIndexedDict &
    IOwnedEntityParameters &
    IGenericRequestParameters<SuggestionsEntity, InferSortByOptions<SuggestionsEntityFilters>>,
>(
  props: GenericVirtualizedEntitySelectFormFieldControllerWrapperProps<
    Entity,
    SuggestionsEntity,
    SuggestionsEntityFilters
  >
) => {
  return (
    <GenericControllerProvider<Entity>
      id={props.id}
      control={props.control}
      defaultValue={props.defaultValue}
      controllerFieldOverride={props.controllerFieldOverride}
    >
      {({ value, onChange }, fieldState, { errors }) => (
        <>
          {props.uncontained ? (
            <FormGroup
              controlId={props.id}
              style={{ margin: 0, width: "100%", height: "100%", overflow: "hidden", ...props.formStyle }}
            >
              <div className="flex" style={{ margin: 0, width: "100%", height: "100%", ...props.style }}>
                {props.prependChildren ? (
                  <div className="flex row-nowrap align-center gap-5">
                    {typeof props.prependChildren === "function"
                      ? props.prependChildren({ value, onChange })
                      : props.prependChildren}
                  </div>
                ) : (
                  <></>
                )}
                <GenericVirtualizedEntitySelectFormField<Entity, SuggestionsEntity, SuggestionsEntityFilters>
                  // key={JSON.stringify(value)} //We trigger a reconciliation (re-render) if the value changes to init new values
                  value={value}
                  onChange={props.onChangeOverride ? (value) => props.onChangeOverride?.(value, onChange) : onChange}
                  onBlur={props.onBlur}
                  error={getError(props.id, errors)}
                  placeholder={props.placeholder}
                  isMulti={props.isMulti}
                  disabled={props.disabled}
                  unassignOnX={props.unassignOnX}
                  allowCreateEntity={props.allowCreateEntity}
                  allowUnassigned={props.allowUnassigned}
                  entityConstants={props.entityConstants}
                  filters={props.filters}
                  orderByOptions={props.orderByOptions}
                  controls={props.controls}
                  onRowRenderer={props.onRowRenderer}
                  showControls={props.showControls}
                  createModal={props.createModal}
                  selectFieldStyle={props.selectFieldStyle}
                  concatItems={props.concatItems}
                  suggestionsIds={props.suggestionsIds}
                  overrideItemRenderer={props.overrideItemRenderer}
                  overrideSelectedRenderer={props.overrideSelectedRenderer}
                  queryResultOverride={props.queryResultOverride}
                />
                {props.children ? (
                  <div className="flex row-nowrap align-center gap-5">
                    {typeof props.children === "function" ? props.children({ value, onChange }) : props.children}
                  </div>
                ) : (
                  <></>
                )}
              </div>
            </FormGroup>
          ) : (
            <FormFieldLayout
              id={props.id}
              label={
                props.label
                  ? props.label
                  : props.isMulti
                    ? toUppercase(props.entityConstants.entityPlural)
                    : toUppercase(props.entityConstants.entitySingular)
              }
              required={props.required}
              horizontal={props.horizontal}
              hasLabel={props.hasLabel}
              style={props.formStyle}
            >
              <div className={styles.container} style={props.style}>
                {props.prependChildren ? (
                  <>
                    {typeof props.prependChildren === "function"
                      ? props.prependChildren({ value, onChange })
                      : props.prependChildren}
                  </>
                ) : (
                  <></>
                )}
                <GenericVirtualizedEntitySelectFormField<Entity, SuggestionsEntity, SuggestionsEntityFilters>
                  // key={JSON.stringify(value)} //We trigger a reconciliation (re-render) if the value changes to init new values
                  value={value}
                  onChange={props.onChangeOverride ? (value) => props.onChangeOverride?.(value, onChange) : onChange}
                  error={getError(props.id, errors)}
                  placeholder={props.placeholder}
                  isMulti={props.isMulti}
                  disabled={props.disabled}
                  unassignOnX={props.unassignOnX}
                  allowCreateEntity={props.allowCreateEntity}
                  allowUnassigned={props.allowUnassigned}
                  entityConstants={props.entityConstants}
                  filters={props.filters}
                  orderByOptions={props.orderByOptions}
                  controls={props.controls}
                  onRowRenderer={props.onRowRenderer}
                  showControls={props.showControls}
                  createModal={props.createModal}
                  selectFieldStyle={props.selectFieldStyle}
                  concatItems={props.concatItems}
                  suggestionsIds={props.suggestionsIds}
                  overrideItemRenderer={props.overrideItemRenderer}
                  overrideSelectedRenderer={props.overrideSelectedRenderer}
                  queryResultOverride={props.queryResultOverride}
                />
                {props.children ? (
                  <div className="flex row-nowrap align-center gap-5">
                    {typeof props.children === "function" ? props.children({ value, onChange }) : props.children}
                  </div>
                ) : (
                  <></>
                )}
              </div>
              <FormFieldError id={props.id} errors={errors} />
            </FormFieldLayout>
          )}
        </>
      )}
    </GenericControllerProvider>
  );
};
